import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // バリデーション
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6b6s2lt1k61917cn8fg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-6 bg-gradient-to-b from-white to-orange-50/30">
      <div className="max-w-7xl mx-auto">
        {/* タイトルエリア */}
        <div className="text-center mb-16">
          <h3 className="text-4xl md:text-5xl font-black text-gray-800 mb-6" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
            まずはお気軽にご相談ください
          </h3>
          <p className="text-base text-gray-600 font-light mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            お電話・メール・Instagramで無料相談を受け付けています
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* 左側：お問い合わせフォーム */}
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg">
            <h4 className="text-2xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
              お問い合わせフォーム
            </h4>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="mb-5">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                  placeholder="山田 太郎"
                />
              </div>
              <div className="mb-5">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                  placeholder="example@email.com"
                />
              </div>
              <div className="mb-5">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                  placeholder="090-1234-5678"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none text-sm"
                  placeholder="ご相談内容をお聞かせください（500文字以内）"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-400 to-pink-400 text-white py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
              {submitStatus === 'success' && (
                <p className="mt-4 text-green-600 text-sm text-center font-medium">送信が完了しました。ありがとうございます。</p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-4 text-red-600 text-sm text-center font-medium">送信に失敗しました。もう一度お試しください。</p>
              )}
            </form>
          </div>

          {/* 右側：連絡先情報 */}
          <div>
            <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-3xl p-8 md:p-10 mb-8">
              <h4 className="text-2xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                お電話でのお問い合わせ
              </h4>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-orange-500"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1 font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>受付時間: 月〜土 8:00〜18:00</p>
                  <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>090-7289-8910</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-8 md:p-10 mb-8">
              <h4 className="text-2xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                Instagramでのお問い合わせ
              </h4>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                  <i className="ri-instagram-line text-2xl text-pink-500"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2 font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>お気軽にメッセージをお送りください</p>
                  <a 
                    href="https://www.instagram.com/sincerity1112/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold hover:from-purple-600 hover:to-pink-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-instagram-fill"></i>
                    @sincerity1112
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg">
              <h4 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                営業時間
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                月曜日〜土曜日: 8:00〜18:00<br />
                定休日: 日曜日・祝日
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}